<script setup>
import { ref, computed, onMounted } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { formatShortDate } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useNavigator } from '@/composables/navigator'
import { useCurrencyColumnFormatter } from '@/composables/data-grid'
import { useChips } from '@/composables/chips'
import { useHighlightNewRow } from '@/composables/scroll'
import { useNotify } from '@/composables/notify'

import ActionsMenuButton from '@/components/shared/ActionsMenuButton.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import ListItem from '@/components/shared/ListItem.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import DescriptionSection from '@/components/shared/DescriptionSection.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import InvoiceEdit from '@/components/accounting/InvoiceEdit.vue'
import InvoiceCommentEdit from '@/components/accounting/InvoiceCommentEdit.vue'
import InvoiceDeleteDialog from '@/components/accounting/InvoiceDeleteDialog.vue'
import InvoiceLineEdit from '@/components/accounting/InvoiceLineEdit.vue'
import InvoicePrintDialog from '@/components/accounting/InvoicePrintDialog.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()
const { invoiceStatusEnum, invoiceLineFieldTypeEnum } = accountingStore
const { notifySuccess } = useNotify()
const { documentRowTypeEnum } = webSiteStore
const { getInvoiceStatusChips, getProjectChips } = useChips()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()

const invoice = ref({ ...props.modelValue })
const rows = ref([])
const rowsContainer = ref(null)
const changed = ref(false)

const editItem = ref(null)
const editStartsOnLines = ref(false)
const showCommentEdit = ref(false)
const showPrintDialog = ref(false)
const invoiceToDelete = ref(null)
const rowToDelete = ref(null)
const editMode = ref(false)
const includeVat = ref(true)
const editedRow = ref(null)
const showStatusDialog = ref(false)
const selectedStatus = ref(null)

const isDraft = computed(() => invoice.value.status === invoiceStatusEnum.DRAFT)

const onNavigateBack = () => emit(changed.value ? 'update' : 'cancel')
useNavigator(onNavigateBack)

const loadInvoiceAsync = async () => {
  const [invoiceResponse, rowsResponse] = await Promise.all([
    accountingStore.fetchInvoiceByIdAsync(props.modelValue.id),
    accountingStore.fetchInvoiceRowsAsync(props.modelValue.id),
  ])
  invoice.value = invoiceResponse.data
  rows.value = rowsResponse.data.sort((a, b) => a.row_number - b.row_number)
}

const reloadAndHighlightNewRowAsync = async () => {
  const previousIds = new Set(rows.value.map(row => row.id))
  changed.value = true
  await loadInvoiceAsync()
  await highlightRowAsync(rows.value.find(row => !previousIds.has(row.id))?.id, rowsContainer)
}

onMounted(loadInvoiceAsync)

const isCommentRow = (row) => row.document_row_type === documentRowTypeEnum.COMMENT

const getUomName = (row) => webSiteStore.getLocalizedUnitsOfMeasure().find(uom => uom.id === row.unit_of_measure_ref)?.name || ''

const getRowLabel = (row) => (isCommentRow(row) ? row.description : row.listing_item_text || row.description)

const getRowLabelDetail = (row) => (isCommentRow(row) ? '' : ` x ${Number(row.quantity)} ${getUomName(row)}`)

const rowFields = [
  { field: 'quantity', label: i18n.t('common.quantity'), type: invoiceLineFieldTypeEnum.QUANTITY },
  { field: 'price', label: i18n.t('common.price'), type: invoiceLineFieldTypeEnum.NUMBER },
  { field: 'description', label: i18n.t('common.description'), type: invoiceLineFieldTypeEnum.TEXT },
]
const commentRowFields = [rowFields.find(field => field.type === invoiceLineFieldTypeEnum.TEXT)]

const getRowSource = (row) => {
  if (row.source_budget_document) return `${row.source_budget_document.name} · ${i18n.t('common.rowNumberAlt')} ${row.source_budget_document_row_number}`
  if (row.source_cost_report_project != null) return `${row.source_cost_report_project_name} · ${formatShortDate(row.source_cost_report_date)}`
  return null
}

const onEditClick = (startOnLines = false) => {
  editStartsOnLines.value = startOnLines
  editItem.value = { ...invoice.value }
}

const onEditUpdate = async () => {
  editItem.value = null
  await reloadAndHighlightNewRowAsync()
}

const onCommentAdded = async () => {
  showCommentEdit.value = false
  await reloadAndHighlightNewRowAsync()
}

const onConfirmDeleteRow = async () => {
  await accountingStore.deleteInvoiceRowAsync(rowToDelete.value.id)
  rowToDelete.value = null
  changed.value = true
  await loadInvoiceAsync()
}

const isRowEditable = computed(() => isDraft.value && editMode.value)

const onRowEdited = async (changes) => {
  const { id } = editedRow.value
  editedRow.value = null
  await accountingStore.upsertInvoiceRowAsync({ id, ...changes })
  notifySuccess(i18n.t('info.updatedSuccessfully'))
  changed.value = true
  await loadInvoiceAsync()
}

const onStatusClick = () => {
  selectedStatus.value = invoice.value.status
  showStatusDialog.value = true
}

const onConfirmStatusChange = async () => {
  await accountingStore.upsertInvoiceAsync({ id: invoice.value.id, status: selectedStatus.value })
  changed.value = true
  await loadInvoiceAsync()
}

const onInvoiceDeleted = () => {
  invoiceToDelete.value = null
  emit('update')
}

const menuActions = computed(() => [
  { key: 'print', label: i18n.t('common.printInvoice'), icon: 'mdi-printer', handler: () => { showPrintDialog.value = true } },
  { key: 'status', label: i18n.t('common.status'), icon: 'mdi-swap-horizontal', handler: onStatusClick },
  { key: 'delete', label: i18n.t('common.delete'), icon: 'mdi-delete-outline', handler: () => { invoiceToDelete.value = invoice.value } },
])
</script>

<template>
  <invoice-delete-dialog
    :invoice="invoiceToDelete"
    @deleted="onInvoiceDeleted"
    @cancel="invoiceToDelete = null"
  />
  <confirm-dialog
    :model-value="rowToDelete !== null"
    :message="i18n.t('common.confirmDelete')"
    icon="mdi-delete-outline"
    icon-color="negative"
    @confirm="onConfirmDeleteRow"
    @cancel="rowToDelete = null"
  />
  <q-dialog v-model="showPrintDialog" maximized persistent>
    <invoice-print-dialog
      v-if="showPrintDialog"
      :invoice-id="invoice.id"
      @cancel="showPrintDialog = false"
    />
  </q-dialog>
  <q-dialog :model-value="editItem !== null" maximized persistent>
    <invoice-edit
      v-if="editItem !== null"
      :model-value="editItem"
      :start-on-lines="editStartsOnLines"
      @update="onEditUpdate"
      @cancel="editItem = null"
    />
  </q-dialog>
  <q-dialog :model-value="editedRow !== null" maximized @hide="editedRow = null">
    <invoice-line-edit
      v-if="editedRow !== null"
      :title="getRowLabel(editedRow)"
      :row="editedRow"
      :fields="isCommentRow(editedRow) ? commentRowFields : rowFields"
      @update="onRowEdited"
      @cancel="editedRow = null"
    />
  </q-dialog>
  <confirm-dialog
    v-model="showStatusDialog"
    :title="i18n.t('common.status')"
    :message="i18n.t('info.invoiceStatusChange')"
    icon="mdi-swap-horizontal"
    @confirm="onConfirmStatusChange"
  >
    <q-select
      v-model="selectedStatus"
      :options="accountingStore.getLocalizedInvoiceStatuses()"
      option-value="id"
      option-label="name"
      emit-value
      map-options
      outlined
    />
  </confirm-dialog>
  <q-dialog v-model="showCommentEdit" maximized persistent>
    <invoice-comment-edit
      v-if="showCommentEdit"
      :invoice="invoice"
      @update="onCommentAdded"
      @cancel="showCommentEdit = false"
    />
  </q-dialog>

  <q-card class="dialog-card-flex">
    <navigate-back
      :text="invoice.invoice_number"
      @cancel="onNavigateBack"
    >
      <template #right>
        <div class="row items-center no-wrap">
          <q-btn
            flat
            round
            icon="mdi-pencil"
            @click="onEditClick()"
          />
          <actions-menu-button :actions="menuActions" />
        </div>
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none">
      <q-card-section class="q-pt-none q-px-sm">
        <q-list>
          <q-item>
            <div style="width: 50%; float: left;">
              <q-item-section>
                <q-item-label caption>{{ i18n.t('common.company') }}</q-item-label>
                <q-item-label>{{ invoice.company_name || '-' }}</q-item-label>
              </q-item-section>
            </div>
            <div style="width: 50%; float: right;">
              <q-item-section class="text-right">
                <q-item-label caption>{{ i18n.t(includeVat ? 'common.totalVat' : 'common.totalNoVat') }}</q-item-label>
                <q-item-label>{{ useCurrencyColumnFormatter(invoice, includeVat ? 'total_vat' : 'total_no_vat', invoice.currency_ref) }}</q-item-label>
              </q-item-section>
            </div>
          </q-item>
          <q-item>
            <div style="width: 50%; float: left;">
              <q-item-section>
                <q-item-label caption>{{ i18n.t('common.status') }}</q-item-label>
                <q-item-label>
                  <chip-template :values="getInvoiceStatusChips(invoice.status)" />
                </q-item-label>
              </q-item-section>
            </div>
            <div style="width: 50%; float: right;">
              <q-item-section class="text-right">
                <q-item-label caption>{{ i18n.t('common.date') }}</q-item-label>
                <q-item-label>
                  <q-icon name="mdi-calendar" class="q-mr-xs" />{{ formatShortDate(invoice.date) }}
                </q-item-label>
              </q-item-section>
            </div>
          </q-item>
          <q-item v-if="invoice.projects?.length">
            <q-item-section>
              <q-item-label caption>{{ i18n.t('common.project', 2) }}</q-item-label>
              <q-item-label>
                <chip-template :values="getProjectChips(invoice.projects)" />
              </q-item-label>
            </q-item-section>
          </q-item>
          <description-section v-if="invoice.payment_terms" :text="invoice.payment_terms" :label="i18n.t('common.paymentTerms')" />
          <description-section v-if="invoice.notes" :text="invoice.notes" :label="i18n.t('common.note', 2)" />
        </q-list>
      </q-card-section>
      <div v-if="!isDraft" class="q-px-md">
        <info-box :message="i18n.t('info.invoiceRowsReadonly')" />
      </div>
      <q-separator />
      <div class="row items-center no-wrap q-gutter-x-sm q-px-md q-pt-sm">
        <q-toggle
          v-model="includeVat"
          :label="i18n.t(includeVat ? 'common.includeVat' : 'common.excludeVat')"
        />
        <q-space />
        <template v-if="isDraft">
          <q-btn
            v-if="rows.length"
            flat
            round
            dense
            :icon="editMode ? 'mdi-pencil-off' : 'mdi-pencil'"
            :color="editMode ? 'primary' : 'grey-6'"
            @click="editMode = !editMode"
          >
            <q-tooltip>{{ editMode ? i18n.t('common.done') : i18n.t('common.edit') }}</q-tooltip>
          </q-btn>
          <q-btn
            style="padding: 0 .5rem;"
            size="sm"
            color="info"
            unelevated
            ripple
            rounded
            no-caps
            icon="mdi-plus-circle-outline"
            @click="showCommentEdit = true"
          >&nbsp;{{ i18n.t('common.documentRowTypes.comment') }}</q-btn>
          <q-btn
            style="padding: 0;"
            size="md"
            unelevated
            ripple
            rounded
            icon="mdi-plus-circle-outline"
            @click="onEditClick(true)"
          />
        </template>
      </div>
      <div ref="rowsContainer" class="q-pa-sm q-px-md">
        <list-item
          v-for="row in rows"
          :key="row.id"
          class="invoice-row row-card-border q-mt-sm q-py-none"
          :class="{ 'row-highlight': row.id === highlightedRowId }"
          hide-avatar
          :right-label="isCommentRow(row) ? '' : useCurrencyColumnFormatter(row, includeVat ? 'total_vat' : 'total_no_vat', invoice.currency_ref)"
          :hide-right-caption="!Number(row.discount)"
        >
          <template v-if="isRowEditable" #actions>
            <q-item-section avatar class="align-middle align-center q-px-none" style="flex-direction: row; gap: .125rem;">
              <q-icon name="mdi-pencil" color="primary" @click.stop="editedRow = row" />
              <q-icon name="mdi-minus-circle-outline" color="negative" @click.stop="rowToDelete = row" />
            </q-item-section>
          </template>
          <template #label>
            <div class="invoice-row-label">
              <span class="text-weight-bolder">{{ getRowLabel(row) }}</span><span>{{ getRowLabelDetail(row) }}</span>
            </div>
          </template>
          <template v-if="getRowSource(row)" #caption>
            <span>{{ getRowSource(row) }}</span>
          </template>
          <template #right-caption>
            <div class="row items-center no-wrap text-negative">
              <q-icon name="mdi-label-percent" class="q-mr-xs" />
              <span class="text-weight-bolder">{{ Number(row.discount) * 100 }}%</span>
            </div>
          </template>
        </list-item>
      </div>
    </q-card-section>
  </q-card>
</template>

<style scoped lang="scss">
.invoice-row {
  padding: .75rem;
  min-width: initial !important;
  align-items: center;
}

.invoice-row-label {
  display: inline-block;
  margin-right: .5rem;
}
</style>
